import React, { useReducer, useRef, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Image, ScrollControls, Scroll, useScroll, Html } from '@react-three/drei';
import { easing } from 'maath';


const material = new THREE.LineBasicMaterial({ color: 'white' });
const geometry = new THREE.BufferGeometry().setFromPoints([
  new THREE.Vector3(0, -0.5, 0),
  new THREE.Vector3(0, 0.5, 0)
]);

// Initial state
const initialState = {
  clicked: null,
  items: []
};

// Reducer function
function galleryReducer(state, action) {
  switch (action.type) {
    case 'SET_ITEMS':
      return { ...state, items: action.payload };
    case 'SET_CLICKED':
      return { ...state, clicked: action.payload };
    case 'RESET_CLICKED':
      return { ...state, clicked: null };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}


const Minimap = ({ state }) => {
  const ref = useRef();
  const scroll = useScroll();
  const { items } = state;
  const { height } = useThree((state) => state.viewport);

  useFrame((state, delta) => {
    ref.current.children.forEach((child, index) => {
      const y = scroll.curve(index / items.length - 1.5 / items.length, 4 / items.length);
      easing.damp(child.scale, 'y', 0.15 + y / 6, 0.15, delta);
    });
  });

  return (
    <group ref={ref}>
      {items.map((_, i) => (
        <line
          key={i}
          geometry={geometry}
          material={material}
          position={[i * 0.06 - items.length * 0.03, -height / 2 + 0.6, 0]}
        />
      ))}
    </group>
  );
};

const GalleryItem = ({ index, position, scale, imgUrl, state, dispatch, onClick }) => {
  const ref = useRef();
  const scroll = useScroll();
  const { clicked, items } = state;
  const [hovered, hover] = useState(false);

  const handleClick = () => {
    dispatch({ type: 'SET_CLICKED', payload: index === clicked ? null : index });
    onClick?.();
  };

  useFrame((state, delta) => {
    const y = scroll.curve(index / items.length - 1.5 / items.length, 4 / items.length);
    easing.damp3(
      ref.current.scale,
      [clicked === index ? 6 : scale[0], clicked === index ? 6 : 4 + y, 1],
      0.15,
      delta
    );

    ref.current.material.scale[0] = ref.current.scale.x;
    ref.current.material.scale[1] = ref.current.scale.y;

    if (clicked !== null && index < clicked) {
      easing.damp(ref.current.position, 'x', position[0] - 2, 0.15, delta);
    }
    if (clicked !== null && index > clicked) {
      easing.damp(ref.current.position, 'x', position[0] + 2, 0.15, delta);
    }
    if (clicked === null || clicked === index) {
      easing.damp(ref.current.position, 'x', position[0], 0.15, delta);

    }

    easing.damp(
      ref.current.material,
      'grayscale',
      hovered || clicked === index ? 0 : Math.max(0, 1 - y),
      0.15,
      delta
    );
    easing.dampC(
      ref.current.material.color,
      hovered || clicked === index ? 'white' : '#aaa',
      hovered ? 0.3 : 0.15,
      delta
    );
  });

  return (
    <group>
      <Image
        ref={ref}
        position={position}
        scale={scale}
        // castShadow
        // receiveShadow
        onClick={handleClick}
        onPointerOver={() => hover(true)}
        onPointerOut={() => hover(false)}
        url={imgUrl}
      />
    </group>
  );
};

const GalleryItems = ({ items, width = 3, gap = 0.15, state, dispatch }) => {
  const { width: viewportWidth } = useThree((state) => state.viewport);
  const xW = width + gap;

  return (
    <ScrollControls
      horizontal
      damping={0.1}
      pages={(viewportWidth - xW + items.length * xW) / viewportWidth}
    >
      <Minimap state={state} />
      <Scroll>
        {items.map((item, i) => (
          <GalleryItem
            key={i}
            index={i}
            position={[i * xW, 0, 0]}
            scale={[width, 6, 1]}
            imgUrl={item.imgUrl}
            state={state}
            dispatch={dispatch}
            onClick={item?.onClick}
          />
        ))}
      </Scroll>
    </ScrollControls>
  );
};

const Gallery3d = ({ items = [], width = 4, gap = 0.25, className = "w-full" }) => {
  const [state, dispatch] = useReducer(galleryReducer, { ...initialState, items });

  return (
    <div className={className} style={{ height: '70vh' }}>
      <Canvas
          shadows
          gl={{antialias: true}}
          camera={{position: [0, 0, 10], fov: 50}}
          onPointerMissed={() => dispatch({type: 'RESET_CLICKED'})}
      >
        <color attach="background" args={['#fff']}/>
        <fog attach="fog" args={['#fff', 0, 15]}/>
        <ambientLight intensity={0.7}/>
        <spotLight
            position={[10, 10, 10]}
            angle={0.2}
            penumbra={1}
            castShadow
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
        />
        <GalleryItems items={items} width={width} gap={gap} state={state} dispatch={dispatch}/>
      </Canvas>
    </div>
  );
};


export default Gallery3d;